import { ScrollLayoutProps } from '@/components/layout/scroll/types';
import { buildBoxStyle } from '@/components/layout/utils';
import { Device } from '@/theme/scaling';
import { FC, useMemo } from 'react';
import { Platform, ScrollView as RNScrollView } from 'react-native';
import { ScrollView as GestureScrollView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

/**
 * Scroll layout component
 * This component is used to create a scroll layout
 * @param children - The children of the scroll
 * @param safeArea - Whether to use safe area
 * @param padding - The padding of the scroll
 * @param paddingX - The padding x of the scroll
 * @param paddingY - The padding y of the scroll
 * @param paddingTop - The padding top of the scroll
 * @param paddingBottom - The padding bottom of the scroll
 * @param paddingLeft - The padding left of the scroll
 * @param paddingRight - The padding right of the scroll
 * @param margin - The margin of the scroll
 * @param marginX - The margin x of the scroll
 * @param marginY - The margin y of the scroll
 * @param marginTop - The margin top of the scroll
 * @param marginBottom - The margin bottom of the scroll
 * @param marginLeft - The margin left of the scroll
 * @param marginRight - The margin right of the scroll
 * @param flex - The flex of the scroll
 * @param flexGrow - The flex grow of the scroll
 * @param flexShrink - The flex shrink of the scroll
 * @param flexBasis - The flex basis of the scroll
 * @param style - The style of the scroll
 * @param contentContainerStyle - The content container style of the scroll
 * @param backgroundColor - The background color of the scroll
 * @param props - The props of the scroll
 * @returns Scroll layout component
 */
export const Scroll: FC<ScrollLayoutProps> = ({
  children,
  safeArea,
  /** padding */
  padding,
  paddingX,
  paddingY,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  /** margin */
  margin,
  marginX,
  marginY,
  marginTop,
  marginBottom,
  marginLeft,
  marginRight,
  /** flex */
  flex,
  flexGrow,
  flexShrink,
  flexBasis,

  /** align */
  align,
  alignSelf,
  justify,

  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  style,
  contentContainerStyle,
  backgroundColor,
  gap,
  gapX,
  gapY,
  gestureEnabled = false,
  ...props
}) => {
  const insets = useSafeAreaInsets();
  const containerStyle = useMemo(
    () =>
      buildBoxStyle({
        margin,
        marginX,
        marginY,
        marginTop,
        marginBottom,
        marginLeft,
        marginRight,
        flex,
        flexGrow,
        flexShrink,
        flexBasis,
        backgroundColor,
        width,
        height,
        minWidth,
        minHeight,
        maxWidth,
        maxHeight,
        gap,
        gapX,
        gapY,
      }),
    [
      backgroundColor,
      flex,
      flexBasis,
      flexGrow,
      flexShrink,
      margin,
      marginBottom,
      marginLeft,
      marginRight,
      marginTop,
      marginX,
      marginY,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      gap,
      gapX,
      gapY,
    ],
  );
  const contentStyle = useMemo(
    () =>
      buildBoxStyle({
        padding,
        paddingX,
        paddingY,
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight,
      }),
    [padding, paddingBottom, paddingLeft, paddingRight, paddingTop, paddingX, paddingY],
  );
  const safeAreaContentStyle = useMemo(
    () =>
      safeArea && {
        paddingBottom: insets.bottom + (Platform.OS === 'android' ? Device.bottomHeight : 0),
        paddingTop: insets.top,
      },
    [insets.bottom, insets.top, safeArea],
  );

  const alignStyle = useMemo(
    () => ({ alignItems: align, alignSelf: alignSelf, justifyContent: justify }),
    [align, alignSelf, justify],
  );

  const ScrollView = gestureEnabled ? GestureScrollView : RNScrollView;

  return (
    <ScrollView
      style={[contentStyle, style]}
      contentContainerStyle={[
        alignStyle,
        containerStyle,
        safeAreaContentStyle,
        contentContainerStyle,
      ]}
      {...props}>
      {children}
    </ScrollView>
  );
};
